import React from 'react';
import { Sparkles, Save, Mail } from 'lucide-react';
import { ANNUAL_DISCOUNT } from '../mock/pricingData';
import { useCurrency } from '../context/CurrencyContext';

const LiveEstimate = ({
  tier,
  billing,
  setBilling,
  addlCreators,
  totalViewers,
  extraViewers,
  extraApps,
  extraTagPacks,
  mrr,
  breakdown,
  historianAnnual,
  dashboardOneTime,
  onSave,
  onEmail,
}) => {
  const isEnterprise = tier.id === 'enterprise';
  const { formatMoney } = useCurrency();
  const annualFromMrr = mrr * 12;
  const annualDiscounted = annualFromMrr * (1 - ANNUAL_DISCOUNT);
  const displayARR = billing === 'annual' ? annualDiscounted : annualFromMrr;
  const displayMRR = billing === 'annual' ? mrr * (1 - ANNUAL_DISCOUNT) : mrr;
  const recurringAnnual = displayARR;
  const totalToPay = recurringAnnual + historianAnnual + dashboardOneTime;

  return (
    <aside className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-white/[0.01] p-6 md:p-7 sticky top-24">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/50">
            Live estimate
          </div>
          <div className="mt-2 text-[22px] font-semibold text-white">
            {tier.name}
          </div>
        </div>
        <div className="relative">
          <div className="absolute inset-0 blur-2xl bg-emerald-400/30 rounded-full" />
          <Sparkles className="relative w-6 h-6 text-emerald-300" />
        </div>
      </div>

      <div className="mt-5 relative inline-flex w-full p-1 rounded-full bg-white/[0.04] border border-white/10">
        <button
          onClick={() => setBilling('monthly')}
          className={`flex-1 h-9 rounded-full text-[12px] font-semibold tracking-wider uppercase transition-colors ${
            billing === 'monthly' ? 'bg-white text-black' : 'text-white/60 hover:text-white/80'
          }`}
        >
          Monthly
        </button>
        <button
          onClick={() => setBilling('annual')}
          className={`flex-1 h-9 rounded-full text-[12px] font-semibold tracking-wider uppercase transition-colors flex items-center justify-center gap-2 ${
            billing === 'annual' ? 'bg-white text-black' : 'text-white/60 hover:text-white/80'
          }`}
        >
          Annual
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${billing === 'annual' ? 'bg-emerald-400/20 text-emerald-500' : 'bg-emerald-400/15 text-emerald-300'}`}>
            −15%
          </span>
        </button>
      </div>

      <div className="mt-6">
        <div className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/50">
          Monthly recurring revenue
        </div>
        <div className="mt-2 flex items-baseline gap-2 flex-wrap">
          {isEnterprise ? (
            <span className="text-4xl font-bold text-emerald-300">Custom</span>
          ) : (
            <span
              className="text-[46px] leading-none font-bold tracking-tight bg-clip-text text-transparent"
              style={{
                backgroundImage: 'linear-gradient(90deg, #22d3ee 0%, #a855f7 100%)',
              }}
            >
              {formatMoney(displayMRR)}
            </span>
          )}
          {!isEnterprise && <span className="text-white/50 text-sm">per month</span>}
        </div>
      </div>

      <div className="mt-6">
        <div className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/50">
          Annual recurring revenue
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          {isEnterprise ? (
            <span className="text-2xl font-semibold text-emerald-300">Contact sales</span>
          ) : (
            <>
              <span className="text-2xl font-semibold text-white tracking-tight">
                {formatMoney(displayARR)}
              </span>
              <span className="text-white/50 text-sm">/yr</span>
            </>
          )}
        </div>
      </div>

      {!isEnterprise && (
        <div className="mt-6 space-y-2 text-[13px]">
          {breakdown.map((row, i) => (
            <div key={i} className="flex items-center justify-between text-white/70 gap-3">
              <span className="min-w-0">{row.label}</span>
              <span className="text-white/90 tabular-nums whitespace-nowrap">{row.value}</span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 rounded-xl border border-emerald-400/25 bg-emerald-400/[0.04] p-4">
        <div className="text-[10px] tracking-[0.22em] font-semibold uppercase text-emerald-300">
          Grand total
        </div>
        <div className="mt-3 flex items-center justify-between text-[13px] text-white/70">
          <span>Recurring annual</span>
          <span className="text-white tabular-nums">
            {isEnterprise ? 'Custom' : `${formatMoney(recurringAnnual)} /yr`}
          </span>
        </div>
        {(historianAnnual > 0 || dashboardOneTime > 0) && (
          <div className="mt-2 space-y-1 text-[12.5px] text-white/60">
            {historianAnnual > 0 && (
              <div className="flex items-center justify-between">
                <span>+ Historian (annual)</span>
                <span className="tabular-nums">{formatMoney(historianAnnual)} /yr</span>
              </div>
            )}
            {dashboardOneTime > 0 && (
              <div className="flex items-center justify-between">
                <span>+ Dashboards (one-time)</span>
                <span className="tabular-nums">{formatMoney(dashboardOneTime)}</span>
              </div>
            )}
          </div>
        )}
        <div className="mt-3 pt-3 border-t border-emerald-400/15 flex items-center justify-between">
          <span className="text-[13px] font-semibold text-white/80">Total to pay</span>
          <span
            className="text-[24px] font-bold tabular-nums bg-clip-text text-transparent"
            style={{
              backgroundImage: 'linear-gradient(90deg, #22d3ee 0%, #a855f7 100%)',
            }}
          >
            {isEnterprise ? 'Custom' : formatMoney(totalToPay)}
          </span>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-[1fr_auto] gap-3">
        <button
          onClick={onSave}
          className="h-11 rounded-lg bg-emerald-400 hover:bg-emerald-300 text-black font-semibold text-sm flex items-center justify-center gap-2 transition-colors shadow-[0_10px_30px_-10px_rgba(52,211,153,0.6)]"
        >
          <Save className="w-4 h-4" />
          Save & get link
        </button>
        <button
          onClick={onEmail}
          className="h-11 px-4 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/80 font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
        >
          <Mail className="w-4 h-4" />
          Email
        </button>
      </div>
    </aside>
  );
};

export default LiveEstimate;
